/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type Nudge } from '@atlassian/ai-smart-button-api';
import { type ConversationAction } from '@atlassian/rovo-conversation-actions';

export const i18n = defineMessages({
	aiProactiveNudgeDismiss: {
		id: 'ai-smart-button.ai-proactive-nudge.dismiss',
		defaultMessage: 'Dismiss',
		description: 'The label text for the dismiss button on the AI Proactive nudge',
	},
});

const ANIMATION_DURATION = 200;
const NUDGE_WIDTH = '350px';

const styles = cssMap({
	wrapperStyles: {
		position: 'absolute',
		left: 0,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		'@media (prefers-reduced-motion)': {
			animation: 'none',
		},
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		borderRadius: token('radius.large'),
		display: 'inline-block',
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('color.background.neutral.bold'),
		transition: `opacity ${ANIMATION_DURATION}ms ease-in-out, transform ${ANIMATION_DURATION}ms ease-in-out`,
		alignItems: 'center',
		justifyContent: 'center',
	},
	wrapperStylesUpdated: {
		position: 'absolute',
		left: 0,
		overflow: 'visible',
		whiteSpace: 'normal',
		height: 'auto',
		maxWidth: NUDGE_WIDTH,
		'@media (prefers-reduced-motion)': {
			animation: 'none',
		},
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		borderRadius: token('radius.large'),
		display: 'inline-block',
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('color.background.neutral.bold'),
		transition: `opacity ${ANIMATION_DURATION}ms ease-in-out, transform ${ANIMATION_DURATION}ms ease-in-out`,
		alignItems: 'left',
		justifyContent: 'left',
	},
	newNudgeIconStyles: {
		color: token('color.icon.inverse'),
	},
	invisibleNudge: {
		opacity: 0,
		transform: `translateX(-100%)`,
	},
	visibleNudge: {
		transform: `translateX(calc(-100% - ${token('space.100')}))`,
		opacity: 1,
	},
	textStyle: {
		overflowWrap: 'normal',
	},

	textWrapperStyle: {
		textAlign: 'left',
		maxWidth: 'max-content',
		width: '200px',
		whiteSpace: 'normal',
	},

	buttonOverride: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > button': {
			height: 'auto',
			minHeight: 'auto',
			paddingBlock: token('space.050'),
		},
	},
});

type NudgeContentProps = {
	activeNudge: Nudge | ConversationAction;
	isNudgeVisible: boolean;
	onDismissClick: () => void;
	onNudgeClick: () => void;
	onWrapperMouseEnter: () => void;
	onWrapperMouseLeave: () => void;
};

export const NudgeContent = memo(
	({
		activeNudge,
		isNudgeVisible,
		onWrapperMouseEnter,
		onWrapperMouseLeave,
		onNudgeClick,
		onDismissClick,
	}: NudgeContentProps) => {
		const intl = useIntl();
		const isTextWrapEnabled = fg('aix_convo_starters_nudge_text_wrap_enabled');

		const NudgeIcon = (activeNudge as Nudge).nudgeIcon ?? (activeNudge as ConversationAction).icon;

		return (
			/**
			 * Wrapper div is used to handle mouse enter and leave events
			 *  because dismiss timer needs to be stopped when
			 * 	user hover over anywhere in nudge.
			 *  Listnening only on button or icon, will not stop timer when
			 * 	hovering over th edges of the nudge.
			 */
			<div
				css={[
					isTextWrapEnabled ? styles.wrapperStylesUpdated : styles.wrapperStyles,
					isNudgeVisible ? styles.visibleNudge : styles.invisibleNudge,
				]}
				data-testid="platform-ai-proactive-nudge"
				// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
				onMouseEnter={onWrapperMouseEnter}
				// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
				onMouseLeave={onWrapperMouseLeave}
			>
				<Box>
					<Inline grow="fill" space="space.025">
						<Stack>
							{isTextWrapEnabled ? (
								<Box xcss={styles.buttonOverride}>
									<Button appearance="subtle" onClick={onNudgeClick}>
										<Inline space="space.075" alignBlock="start">
											<div css={styles.newNudgeIconStyles}>
												<NudgeIcon label="" />
											</div>
											<Box xcss={styles.textWrapperStyle}>
												<Text color={'color.text.inverse'} xcss={styles.textStyle}>
													{activeNudge.title}
												</Text>
											</Box>
										</Inline>
									</Button>
								</Box>
							) : (
								<Button appearance="subtle" onClick={onNudgeClick}>
									<Inline space="space.075" alignBlock="end">
										<div css={styles.newNudgeIconStyles}>
											<NudgeIcon label="" />
										</div>
										<Text color={'color.text.inverse'}>{activeNudge.title}</Text>
									</Inline>
								</Button>
							)}
						</Stack>
						<IconButton
							testId="platform-ai-proactive-nudge-dismiss"
							appearance="subtle"
							label={intl.formatMessage(i18n.aiProactiveNudgeDismiss)}
							isTooltipDisabled={false}
							tooltip={{ position: 'top' }}
							icon={(iconProps) => (
								<CrossIcon {...iconProps} size="small" color={token('color.icon.inverse')} />
							)}
							onClick={onDismissClick}
						/>
					</Inline>
				</Box>
			</div>
		);
	},
);
